import store from '@/store';
import { productOrderService } from '@/service/ProductOrderService';
import { notificationMatchesSelectedRole } from '@/utils';
import { notificationMatchesZoneOrRangeFilter } from '@/utils/unassignedReservationZone';

/**
 * Resolves the active range table-name set from Vuex (same source as Copilot audio).
 * @returns {Set<string>}
 */
export function getSelectedRangeTableNamesSet() {
	const raw = store.getters['copilot/selectedRangeTableNames'] || [];
	return raw instanceof Set ? raw : new Set(raw);
}

/**
 * Single gate for “should this notification be audible / visible under the current
 * role + partidas + zone/range selection?”.
 *
 * @param {unknown} notification
 * @param {{
 *   selectedRole?: { id: unknown, notificationTypes?: { id: unknown }[] } | null,
 *   selectedZones?: string[],
 *   allowedTables?: Set<string>|string[]|null,
 * }=} [context] Optional overrides (tests); defaults read from Vuex.
 * @returns {boolean}
 */
export function isNotificationPlayable(notification, context = {}) {
	if (!notification) {
		return false;
	}

	const selectedRole =
		context.selectedRole !== undefined
			? context.selectedRole
			: store.getters['copilot/getSelectedRole'];

	if (!notificationMatchesSelectedRole(notification, selectedRole)) {
		return false;
	}

	if (!productOrderService.isNotificationRelevantForStationFilter(notification)) {
		return false;
	}

	const selectedZones =
		context.selectedZones !== undefined
			? context.selectedZones
			: store.getters['copilot/selectedZoneFilter'] || [];

	const allowedTables =
		context.allowedTables !== undefined
			? context.allowedTables instanceof Set
				? context.allowedTables
				: new Set(context.allowedTables || [])
			: getSelectedRangeTableNamesSet();

	if ((selectedZones?.length || 0) === 0 && allowedTables.size === 0) {
		return true;
	}

	return notificationMatchesZoneOrRangeFilter(notification, selectedZones, allowedTables);
}

/**
 * Filters a notification list with the same gate used for live audio.
 * @param {unknown[]} notifications
 * @param {Parameters<typeof isNotificationPlayable>[1]=} [context]
 * @returns {unknown[]}
 */
export function filterNotificationsByPlayableGate(notifications, context) {
	const list = Array.isArray(notifications) ? notifications : [];
	return list.filter((notification) => isNotificationPlayable(notification, context));
}
